import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/** The theme actually applied (`data-theme` on <html>). */
type Theme = 'light' | 'dark';
/** What the user picked: a fixed theme, or follow the OS (`Auto` in Preferences). */
export type ThemePreference = Theme | 'auto';

interface ThemeContextValue {
  theme: Theme;
  preference: ThemePreference;
  setTheme: (preference: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const DARK_QUERY = '(prefers-color-scheme: dark)';

const systemTheme = (): Theme =>
  typeof window !== 'undefined' && window.matchMedia?.(DARK_QUERY)?.matches ? 'dark' : 'light';

/**
 * Accepts the stored/settings spellings ('Light' | 'Dark' | 'Auto', any case;
 * 'system' too) and returns a preference. Anything else is 'light'.
 */
export const parseThemePreference = (raw: string | null | undefined): ThemePreference => {
  const v = (raw ?? '').toLowerCase();
  if (v === 'dark') return 'dark';
  if (v === 'auto' || v === 'system') return 'auto';
  return 'light';
};

// localStorage keeps the Preferences spelling; public/index.html reads it before
// React loads to avoid a flash of the wrong theme.
const STORED: Record<ThemePreference, string> = { light: 'Light', dark: 'Dark', auto: 'Auto' };

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preference, setPreference] = useState<ThemePreference>(() => {
    try {
      return parseThemePreference(localStorage.getItem('theme'));
    } catch {
      return 'light';
    }
  });
  const [system, setSystem] = useState<Theme>(systemTheme);

  // Follow OS changes live (only matters while the preference is 'auto').
  useEffect(() => {
    const mql = window.matchMedia?.(DARK_QUERY);
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  const theme: Theme = preference === 'auto' ? system : preference;

  const toggleTheme = () => {
    setPreference(theme === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem('theme', STORED[preference]);
    } catch {
      // storage blocked — the theme still applies for this visit
    }
  }, [preference]);

  return (
    <ThemeContext.Provider value={{ theme, preference, setTheme: setPreference, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
